import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { quotas } from '../../pages/app/protected/services/QuotasConfig';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

export default function ReceiptHistory() {
  const { user, companyId } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [companySnap, setCompanySnap] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);
  const perPage = 10;
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };
  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const normalizeStatus = (status) => (status || '').toString().trim().toLowerCase();
  const isVoidedOrder = (order) => normalizeStatus(order.paymentStatus) === 'void';

  const loadPlan = async () => {
    const docRef = doc(db, 'companies', companyId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      setPlan(docSnap.data()?.subscription?.plan || 'Free Trial');
    }
  };

  useEffect(() => {
    loadPlan();
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  const filteredOrders = orders.filter((order) => {
    if (isVoidedOrder(order)) return false;
    const orderDate = parseTimestamp(order.timestamp);
    if (!orderDate) return false;
    const from = fromDate ? new Date(fromDate) : new Date(0);
    const to = toDate ? new Date(toDate) : new Date();
    to.setHours(23, 59, 59, 999);

    const matchesSearch = 
      String(order.receiptNumber).includes(search) ||
      order.paymentMethod?.toLowerCase().includes(search.toLowerCase()) ||
      order.mode?.toLowerCase().includes(search.toLowerCase());
    
    const matchesDate = orderDate >= from && orderDate <= to;
    return matchesSearch && matchesDate;
  });

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / perPage));
  const paginated = filteredOrders.slice((currentPage - 1) * perPage, currentPage * perPage);

  const loadOrders = async () => {
    if (!user || !companyId) return;
    const q = query(
      collection(db, 'companies', companyId, 'orders'),
      orderBy('timestamp', 'desc')
    );
    const snapshot = await getDocs(q);
    const list = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter((o) => !isVoidedOrder(o));
    setOrders(list);
  };

  const loadCompany = async () => {
    const profileRef = doc(db, 'companies', companyId, 'companyProfile', 'info');
    const profileSnap = await getDoc(profileRef);
    if (profileSnap.exists()) {
      setCompanySnap(profileSnap.data());
    }
  };

  const loadProducts = async () => {
    if (!user || !companyId) return;
    const snap = await getDocs(collection(db, 'companies', companyId, 'inventory', 'data', 'products'));
    setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}`;
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDateTime = (date) => {
    return `${formatDate(date)} ${formatTime(date)}`;
  };

  const productMapById = useMemo(() => {
    const map = {};
    products.forEach(p => {
      if (p.id) map[p.id] = p;
    });
    return map;
  }, [products]);

  const productSkuLookup = useMemo(() => {
    const map = {};
    products.forEach(p => {
      if (p.id) map[p.id] = p.sku || '';
      if (p.variants && Array.isArray(p.variants)) {
        p.variants.forEach(v => {
          const vid = v.id || v.variantId;
          if (vid) {
            map[`${p.id}::${vid}`] = v.sku || v.variantSku || v.barcode || p.sku || '';
          }
        });
      }
    });
    return map;
  }, [products]);

  const resolveSku = (item) => {
    if (item.type === 'bundle') {
      return item.sku || 'BUNDLE';
    }
    const variantId = item.variantId || item.variant_id;
    if (item.id && variantId) {
      return productSkuLookup[`${item.id}::${variantId}`] || productSkuLookup[item.id] || item.sku || '-';
    }
    if (item.id) {
      return productSkuLookup[item.id] || item.sku || '-';
    }
    return item.sku || '-';
  };

  const formatVariant = (item) => {
    if (item.variantName) return item.variantName;
    const opts = item.selectedOptions || item.options;
    if (Array.isArray(opts) && opts.length > 0) {
      return opts.map(o => `${o.optionLabel || o.optKey || o.group}: ${o.valueLabel || o.valueKey || o.value}`).join(' / ');
    }
    return '';
  };

  const formatVariantFromInventory = (item) => {
    const prod = item.id ? productMapById[item.id] : null;
    if (!prod) return '';
    const variantId = item.variantId || item.variant_id;
    if (!variantId || !Array.isArray(prod.variants)) return '';
    const v = prod.variants.find(opt => opt.id === variantId || opt.variantId === variantId);
    if (!v) return '';
    if (Array.isArray(v.options) && v.options.length > 0) {
      return v.options.map(o => `${o.group}: ${o.value}`).join(' / ');
    }
    return v.name || v.label || '';
  };

  const formatVariantDisplay = (item) => {
    return formatVariant(item) || formatVariantFromInventory(item);
  };

  // --------------------------
  // Intra-day analytics
  // --------------------------
  const hourBounds = useMemo(() => {
    let min = 24;
    let max = -1;
    filteredOrders.forEach(order => {
      const ts = parseTimestamp(order.timestamp);
      if (!ts) return;
      const h = ts.getHours();
      if (h < min) min = h;
      if (h > max) max = h;
    });
    if (min === 24 || max === -1) return { min: 0, max: 23 };
    return { min, max };
  }, [filteredOrders]);

  const hoursRange = useMemo(() => {
    const len = hourBounds.max - hourBounds.min + 1;
    return Array.from({ length: len }, (_, i) => hourBounds.min + i);
  }, [hourBounds]);
  const intraDayData = useMemo(() => {
    const byDate = {};
    filteredOrders.forEach(order => {
      const ts = parseTimestamp(order.timestamp);
      if (!ts) return;
      const dateKey = ts.toISOString().split('T')[0];
      const hour = ts.getHours();
      if (hour < 0 || hour > 23) return;
      if (!byDate[dateKey]) {
        byDate[dateKey] = { total: 0, hourly: {} };
      }
      const amount = toNumber(order.total);
      byDate[dateKey].total += amount;
      byDate[dateKey].hourly[hour] = (byDate[dateKey].hourly[hour] || 0) + amount;
    });
    return byDate;
  }, [filteredOrders]);

  const intraChartData = useMemo(() => {
    const labels = hoursRange.map(h => `${h}:00`);
    const palette = ['#f59e0b', '#0ea5e9', '#10b981', '#eab308', '#a855f7', '#ef4444', '#14b8a6', '#8b5cf6'];
    const datasets = Object.entries(intraDayData).map(([date, { hourly }], idx) => ({
      label: date,
      data: hoursRange.map(h => toNumber(hourly[h] || 0)),
      borderColor: palette[idx % palette.length],
      backgroundColor: 'transparent',
      tension: 0.25,
      pointRadius: 3,
      pointBorderWidth: 2,
      pointBackgroundColor: palette[idx % palette.length],
    }));
    return { labels, datasets };
  }, [intraDayData, hoursRange]);

  const intraChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { color: '#e5e7eb', boxWidth: 14 } },
      tooltip: {
        callbacks: { label: (ctx) => `RM ${toNumber(ctx.parsed.y).toFixed(2)}` },
        displayColors: true,
        titleColor: '#0b0f19',
        bodyColor: '#0b0f19',
        backgroundColor: '#f9fafb',
        borderColor: '#0b0f19',
        borderWidth: 1,
      },
      datalabels: { display: false },
    },
    scales: {
      x: {
        ticks: { color: '#e5e7eb' },
        grid: { color: 'rgba(229,231,235,0.08)' },
        title: { display: true, text: 'Hour of Day', color: '#e5e7eb' },
      },
      y: {
        ticks: { color: '#e5e7eb', callback: (v) => `RM ${v}` },
        grid: { color: 'rgba(229,231,235,0.08)' },
        beginAtZero: true,
        title: { display: true, text: 'Total Sales (RM)', color: '#e5e7eb' },
      },
    },
  }), []);

  const intraMetrics = useMemo(() => {
    const totalSales = filteredOrders.reduce((sum, o) => sum + toNumber(o.total), 0);
    const totalTx = filteredOrders.length;
    const avg = totalTx ? totalSales / totalTx : 0;
    let peakDay = '';
    let peakSales = 0;
    Object.entries(intraDayData).forEach(([date, { total }]) => {
      if (total > peakSales) {
        peakSales = total;
        peakDay = date;
      }
    });
    return {
      totalSales,
      totalTx,
      avg,
      peakDay,
      peakSales,
    };
  }, [filteredOrders, intraDayData]);

  const exportToExcel = () => {
    if (!quotas[plan]?.exportPDF) {
      alert('Export is only available for Plus and Premium plans.');
      return;
    }

    // Create CSV content with one product per row
    let csvContent = 'Receipt #,Date,Time,Mode,Payment Method,SKU,Product Name,Quantity,Unit Price,Item Total,Subtotal,SST 6%,SST 8%,Service Tax Rate,Service Tax Amount,Discount,Rounding,Total,Status\n';

    filteredOrders.forEach(order => {
      const orderData = {
        receiptNumber: `#${order.receiptNumber || '-'}`,
        date: formatDate(order.timestamp),
        time: formatTime(order.timestamp),
        mode: order.mode || '-',
        paymentMethod: order.paymentMethod || '-',
        subtotal: order.subtotal?.toFixed(2) || '0.00',
        sst6Amount: order.sst6Amount?.toFixed(2) || '0.00',
        sst8Amount: order.sst8Amount?.toFixed(2) || '0.00',
        serviceTax10Amount: order.serviceTax10Amount?.toFixed(2) || '0.00',
        serviceTax10Rate: Number(order.serviceTax10Rate || 0).toFixed(2) + '%',
        discountAmount: order.discountAmount?.toFixed(2) || '0.00',
        rounding: order.rounding || '0.00',
        total: order.total?.toFixed(2) || '0.00',
        paymentStatus: order.paymentStatus || '-'
      };

      // Create one row per item
      if (order.items && order.items.length > 0) {
        order.items.forEach((item, index) => {
          const qty = item.qty || item.quantity || 1;
          const itemTotal = (qty * item.price).toFixed(2);
          const sku = resolveSku(item);
          const variantLabel = formatVariantDisplay(item);
          
          const row = [
            index === 0 ? orderData.receiptNumber : '', // Only show on first item
            index === 0 ? orderData.date : '',
            index === 0 ? orderData.time : '',
            index === 0 ? orderData.mode : '',
            index === 0 ? orderData.paymentMethod : '',
            sku,
            `"${variantLabel ? `${item.name} (Variant: ${variantLabel})` : item.name}"`,
            qty,
            item.price?.toFixed(2) || '0.00',
            itemTotal,
            index === 0 ? orderData.subtotal : '',
            index === 0 ? orderData.sst6Amount : '',
            index === 0 ? orderData.sst8Amount : '',
            index === 0 ? orderData.serviceTax10Rate : '',
            index === 0 ? orderData.serviceTax10Amount : '',
            index === 0 ? orderData.discountAmount : '',
            index === 0 ? orderData.rounding : '',
            index === 0 ? orderData.total : '',
            index === 0 ? orderData.paymentStatus : ''
          ];

          csvContent += row.join(',') + '\n';
        });
      } else {
        // If no items, still create a row
        const row = [
          orderData.receiptNumber,
          orderData.date,
          orderData.time,
          orderData.mode,
          orderData.paymentMethod,
          '-',
          'No items',
          '0',
          '0.00',
          '0.00',
          orderData.subtotal,
          orderData.sst6Amount,
          orderData.sst8Amount,
          orderData.serviceTax10Amount,
          orderData.discountAmount,
          orderData.rounding,
          orderData.total,
          orderData.paymentStatus
        ];

        csvContent += row.join(',') + '\n';
      }
    });

    // Append metrics summary
    csvContent += '\nMetric,Value\n';
    csvContent += `Total Sales (RM),${intraMetrics.totalSales.toFixed(2)}\n`;
    csvContent += `Total Transactions,${intraMetrics.totalTx}\n`;
    csvContent += `Average Sale per Transaction,${intraMetrics.avg.toFixed(2)}\n`;
    csvContent += `Peak Selling Day,${intraMetrics.peakDay || '-'}\n`;
    csvContent += `Peak Sales (RM),${intraMetrics.peakSales.toFixed(2)}\n`;

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `sales_overview${formatDate(fromDate).replace(/\//g, '-')}_to_${formatDate(toDate).replace(/\//g, '-')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    if (!quotas[plan]?.exportPDF) {
      alert('Export to PDF is only available for Plus and Premium plans.');
      return;
    }

    const docPDF = new jsPDF('landscape');
    const fromFormatted = formatDate(fromDate);
    const toFormatted = formatDate(toDate);
    const title = `Sales Overview Report (${fromFormatted} - ${toFormatted})`;

    docPDF.setFontSize(12);
    docPDF.text(companySnap.companyName || 'Company Name', 148, 15, { align: 'center' });
    docPDF.setFontSize(10);
    docPDF.text(title, 148, 22, { align: 'center' });

    let nextY = 30;
    try {
      const chartCanvas = document.querySelector('.company-intraday-chart canvas');
      if (chartCanvas) {
        const imgData = chartCanvas.toDataURL('image/png', 1.0);
        const pageWidth = docPDF.internal.pageSize.getWidth() - 20;
        const imgHeight = 80;
        docPDF.addImage(imgData, 'PNG', 10, nextY, pageWidth, imgHeight);
        nextY += imgHeight + 6;
      }
    } catch (err) {
      console.warn('Could not render intra-day chart for PDF:', err);
    }

    // Metrics table
    autoTable(docPDF, {
      head: [['Metric', 'Value']],
      body: [
        ['Total Sales (RM)', intraMetrics.totalSales.toFixed(2)],
        ['Total Transactions', intraMetrics.totalTx],
        ['Average Sale per Transaction', intraMetrics.avg.toFixed(2)],
        ['Peak Selling Day', intraMetrics.peakDay || '-'],
        ['Peak Sales (RM)', intraMetrics.peakSales.toFixed(2)],
      ],
      startY: nextY,
      styles: { fontSize: 8 },
      theme: 'grid',
    });
    nextY = docPDF.lastAutoTable.finalY + 8;

    const headers = [['Receipt #', 'Date & Time', 'Mode', 'Payment', 'Items', 'Total', 'Status']];
    const data = filteredOrders.map((order) => [
      `#${order.receiptNumber || '-'}`,
      formatDateTime(order.timestamp),
      order.mode || '-',
      order.paymentMethod || '-',
      order.items?.length || 0,
      `RM ${order.total?.toFixed(2) || '0.00'}`,
      order.paymentStatus || '-',
    ]);

    autoTable(docPDF, {
      head: headers,
      body: data,
      startY: nextY,
      styles: { fontSize: 7 },
    });

    const pageHeight = docPDF.internal.pageSize.height;
    docPDF.setFontSize(9);
    docPDF.text('Powered by CashMate', 148, pageHeight - 10, { align: 'center' });

    const cleanFilename = `sales_overview${fromFormatted.replace(/\//g, '-')}_to_${toFormatted.replace(/\//g, '-')}.pdf`;
    docPDF.save(cleanFilename);
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadOrders(), loadCompany(), loadProducts()]);
      setLoading(false);
    };
    loadAll();
  }, [user]);

  if (loading) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading overview reports...</span>
      </div>
    );
  }

  return (
    <div className="p-6 text-white">
      <div className="flex items-center mb-4 gap-2">
        <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-gray-300 hover:text-white">
          <span className="text-lg">←</span> Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-4">Sales Overview</h2>

      <div className="flex flex-col lg:flex-row gap-4 mb-4">
        <div className="flex gap-2 flex-wrap">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 bg-[#1f1f1f] rounded text-white text-sm"
          />
          <span className="text-gray-400 flex items-center">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 bg-[#1f1f1f] rounded text-white text-sm"
          />
        </div>
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 bg-[#1f1f1f] rounded text-white text-sm flex-1"
        />
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 px-3 py-2 rounded transition text-sm whitespace-nowrap">
            Export Excel
          </button>
          <button onClick={exportToPDF} className="bg-purple-600 hover:bg-purple-700 px-3 py-2 rounded transition text-sm whitespace-nowrap">
            Export PDF
          </button>
        </div>
      </div>

      <div className="bg-[#1a1a1a] rounded-lg p-4 mb-6 company-intraday-chart">
        <h3 className="text-lg font-semibold mb-3">Intra-day Sales Trend</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-[#111] rounded-md p-3">
            <div className="text-xs text-gray-400">Total Sales (RM)</div>
            <div className="text-xl font-bold text-white">RM {intraMetrics.totalSales.toFixed(2)}</div>
          </div>
          <div className="bg-[#111] rounded-md p-3">
            <div className="text-xs text-gray-400">Total Transactions</div>
            <div className="text-xl font-bold text-white">{intraMetrics.totalTx}</div>
          </div>
          <div className="bg-[#111] rounded-md p-3">
            <div className="text-xs text-gray-400">Avg Sale / Tx</div>
            <div className="text-xl font-bold text-white">RM {intraMetrics.avg.toFixed(2)}</div>
          </div>
          <div className="bg-[#111] rounded-md p-3">
            <div className="text-xs text-gray-400">Peak Selling Day</div>
            <div className="text-lg font-semibold text-white">{intraMetrics.peakDay || '-'}</div>
          </div>
          <div className="bg-[#111] rounded-md p-3">
            <div className="text-xs text-gray-400">Peak Sales (RM)</div>
            <div className="text-xl font-bold text-white">RM {intraMetrics.peakSales.toFixed(2)}</div>
          </div>
        </div>
        <div className="h-80 w-full">
          <Line data={intraChartData} options={intraChartOptions} />
        </div>
      </div>

      <div className="bg-[#1a1a1a] rounded-lg overflow-x-auto">
        <table className="w-full text-xs md:text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="px-2 md:px-4 py-3 whitespace-nowrap">Receipt #</th>
              <th className="px-2 md:px-4 py-3 whitespace-nowrap">Date & Time</th>
              <th className="px-2 md:px-4 py-3 whitespace-nowrap">Mode</th>
              <th className="px-2 md:px-4 py-3 whitespace-nowrap">Payment</th>
              <th className="px-2 md:px-4 py-3 whitespace-nowrap">Total</th>
              <th className="px-2 md:px-4 py-3 whitespace-nowrap">Status</th>
              <th className="px-2 md:px-4 py-3 whitespace-nowrap">Details</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((order) => (
              <React.Fragment key={order.id}>
                <tr className="border-t border-gray-700 hover:bg-[#252525] transition">
                  <td className="px-2 md:px-4 py-3 whitespace-nowrap">#{order.receiptNumber || '-'}</td>
                  <td className="px-2 md:px-4 py-3 whitespace-nowrap">
                    <div className="text-xs md:text-sm">{formatDate(order.timestamp)}</div>
                    <div className="text-[10px] md:text-xs text-gray-400">{formatTime(order.timestamp)}</div>
                  </td>
                  <td className="px-2 md:px-4 py-3 whitespace-nowrap">{order.mode || '-'}</td>
                  <td className="px-2 md:px-4 py-3 whitespace-nowrap">{order.paymentMethod || '-'}</td>
                  <td className="px-2 md:px-4 py-3 font-bold whitespace-nowrap">RM {order.total?.toFixed(2) || '0.00'}</td>
                  <td className="px-2 md:px-4 py-3">
                    <span className={`px-2 py-1 rounded text-[10px] md:text-xs whitespace-nowrap ${
                      order.paymentStatus === 'Paid' ? 'bg-green-600' :
                      order.paymentStatus === 'Void' ? 'bg-red-600' :
                      'bg-yellow-600'
                    }`}>
                      {order.paymentStatus || '-'}
                    </span>
                  </td>
                  <td className="px-2 md:px-4 py-3">
                    <button
                      onClick={() => setExpandedRow(expandedRow === order.id ? null : order.id)}
                      className="text-purple-400 hover:text-purple-300 transition text-xs whitespace-nowrap"
                    >
                      {expandedRow === order.id ? '▼' : '▶'}
                    </button>
                  </td>
                </tr>
                {expandedRow === order.id && (
                  <tr className="border-t border-gray-700 bg-[#151515]">
                    <td colSpan="8" className="px-2 md:px-4 py-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-full overflow-hidden">
                        <div className="overflow-hidden">
                          <h4 className="font-semibold text-purple-400 mb-2 text-sm">Order Items</h4>
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="border-b border-gray-700 pb-2">
                                <div className="flex justify-between text-xs md:text-sm gap-2">
                                  <div className="flex-1">
                                    <span className="truncate block font-medium">{item.name}</span>
                                    {formatVariantDisplay(item) && (
                                      <span className="block text-[10px] md:text-xs text-gray-400">
                                        Variant: {formatVariantDisplay(item)}
                                      </span>
                                    )}
                                  </div>
                                  <span className="whitespace-nowrap">RM {((item.qty || item.quantity || 1) * item.price).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-[10px] md:text-xs text-gray-400 mt-1">
                                  <span>SKU: {resolveSku(item)}</span>
                                  <span>Qty: {item.qty || item.quantity || 1} × RM{item.price?.toFixed(2) || '0.00'}</span>
                                </div>
                                {item.items && Array.isArray(item.items) && (
                                  <div className="mt-2 ml-2 space-y-1">
                                    {item.items.map((sub, sIdx) => (
                                      <div key={sIdx} className="text-[10px] md:text-xs text-gray-300 flex justify-between">
                                        <span className="flex-1">
                                          <span className="block">{sub.name}</span>
                                          {formatVariantDisplay(sub) && (
                                            <span className="block text-gray-500">
                                              Variant: {formatVariantDisplay(sub)}
                                            </span>
                                          )}
                                        </span>
                                        <span className="whitespace-nowrap text-right">
                                          Qty: {toNumber(sub.quantity || 1) * toNumber(item.qty || item.quantity || 1)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )) || <span className="text-gray-400 text-xs">No items</span>}
                          </div>
                        </div>
                        <div className="overflow-hidden">
                          <h4 className="font-semibold text-purple-400 mb-2 text-sm">Payment Details</h4>
                          <div className="space-y-1 text-xs md:text-sm">
                            <div className="flex justify-between">
                              <span>Subtotal:</span>
                              <span>RM {order.subtotal?.toFixed(2) || '0.00'}</span>
                            </div>
                            {order.sst6Amount > 0 && (
                              <div className="flex justify-between">
                                <span>SST 6%:</span>
                                <span>RM {order.sst6Amount?.toFixed(2)}</span>
                              </div>
                            )}
                            {order.sst8Amount > 0 && (
                              <div className="flex justify-between">
                                <span>SST 8%:</span>
                                <span>RM {order.sst8Amount?.toFixed(2)}</span>
                              </div>
                            )}
                            {order.serviceTax10Amount > 0 && (
                              <div className="flex justify-between">
                                <span>Service Tax {order.serviceTax10Rate ? `(${Number(order.serviceTax10Rate)}%)` : ''}:</span>
                                <span>RM {order.serviceTax10Amount?.toFixed(2)}</span>
                              </div>
                            )}
                            {order.discountAmount > 0 && (
                              <div className="flex justify-between text-green-400">
                                <span>Discount ({order.discount}):</span>
                                <span>-RM {order.discountAmount?.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span>Rounding:</span>
                              <span>RM {order.rounding || '0.00'}</span>
                            </div>
                            <div className="flex justify-between font-bold text-lg border-t border-gray-600 pt-1 mt-1">
                              <span>Total:</span>
                              <span>RM {order.total?.toFixed(2) || '0.00'}</span>
                            </div>
                            {order.received && (
                              <>
                                <div className="flex justify-between">
                                  <span>Received:</span>
                                  <span>RM {order.received?.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Balance:</span>
                                  <span>RM {order.balance?.toFixed(2)}</span>
                                </div>
                              </>
                            )}
                            {order.cardRefNum && order.paymentMethod === 'Debit/Credit' && (
                              <div className="flex justify-between">
                                <span>Card Ref #:</span>
                                <span>{order.cardRefNum}</span>
                              </div>
                            )}
                            {order.deliveryNum && (
                              <div className="flex justify-between">
                                <span>Delivery #:</span>
                                <span>{order.deliveryNum}</span>
                              </div>
                            )}
                            {order.queueNumber && (
                              <div className="flex justify-between">
                                <span>Queue #:</span>
                                <span>{order.queueNumber}</span>
                              </div>
                            )}
                            {order.orderNumber && (
                              <div className="flex justify-between">
                                <span>Order #:</span>
                                <span>{order.orderNumber}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {paginated.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-400" colSpan={8}>
                  No receipts found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center flex-wrap mt-6 gap-2">
        <button
          className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50 hover:bg-[#3c3c3c] transition"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(p => p - 1)}
        >
          Prev
        </button>
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={`px-4 py-2 rounded transition ${
              currentPage === i + 1
                ? 'bg-purple-600 text-white'
                : 'bg-[#2c2c2c] text-gray-300 hover:bg-[#3c3c3c]'
            }`}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </button>
        ))}
        <button
          className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50 hover:bg-[#3c3c3c] transition"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage(p => p + 1)}
        >
          Next
        </button>
      </div>

      <div className="mt-4 text-sm text-gray-400">
        Showing {paginated.length} of {filteredOrders.length} receipts
      </div>
    </div>
  );
}
